import React from "react";
import {
  PLH_MISSIONS_MOCK,
  TOTAL_XP,
  LEVELS,
  REWARDS,
  LAST_WEEK_ITEMS,
  type MissionItem,
} from "./plhMissionsMock";

const DIFF_LABEL: Record<string, string> = { easy: "Easy", med: "Medium", hard: "Hard" };

export interface MissionsTileProps {
  completedSet: Set<number>;
  missionNotes: Record<number, string>;
  onToggleDone: (index: number) => void;
  onMissionClick: (index: number) => void;
}

export function MissionsTile({
  completedSet,
  missionNotes,
  onToggleDone,
  onMissionClick,
}: MissionsTileProps) {
  const missions = PLH_MISSIONS_MOCK;
  const total = missions.length;
  const doneCount = completedSet.size;
  const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;
  const earned = missions.reduce((s, m, i) => s + (completedSet.has(i) ? m.xp : 0), 0);
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (pct / 100) * circumference;
  const levelIndex = Math.min(Math.floor(doneCount / 1.2), LEVELS.length - 1);

  return (
    <div className="missions-section">
      <div className="sec-hdr">
        Weekly Missions <span className="sec-badge">✦ AI-generated · click for details</span>
      </div>
      <div className="missions-wrap">
        <div className="missions-progress">
          <div className="ring-wrap">
            <svg width={96} height={96} viewBox="0 0 96 96" aria-hidden>
              <circle className="ring-bg" cx={48} cy={48} r={40} fill="none" strokeWidth={7} />
              <circle
                className="ring-fg"
                cx={48}
                cy={48}
                r={40}
                fill="none"
                strokeWidth={7}
                strokeLinecap="round"
                strokeDasharray={circumference}
                style={{ strokeDashoffset }}
                transform="rotate(-90 48 48)"
              />
            </svg>
            <div className="ring-center">
              <div className="ring-pct">{pct}%</div>
              <div className="ring-label">Done</div>
            </div>
          </div>
          <div className="xp-total">{earned.toLocaleString()} XP</div>
          <div className="xp-sub">
            of <span>{TOTAL_XP.toLocaleString()}</span> XP
          </div>
          <div className="xp-level">{LEVELS[levelIndex]}</div>
          <div className="xp-reward">
            {levelIndex < LEVELS.length - 1
              ? `Next: ${LEVELS[levelIndex + 1]} → ${REWARDS[levelIndex + 1]}`
              : "🏆 All levels unlocked!"}
          </div>
          <div className="streak-badge">🔥 Week 1</div>
          <div className="last-week">
            <div className="lw-title">✦ AI Auto-eval · Last Week</div>
            {LAST_WEEK_ITEMS.map((item, i) => (
              <div key={i} className={`lw-item lw-${item.status}`}>
                <span className="lw-icon">
                  {item.status === "ok" ? "✓" : item.status === "miss" ? "✗" : "◐"}
                </span>
                {item.label}
              </div>
            ))}
          </div>
        </div>
        <div className="missions-list">
          {missions.map((m: MissionItem, i: number) => {
            const done = completedSet.has(i);
            const note = missionNotes[i];
            const dots = Array.from({ length: 5 }, (_, j) => (
              <span key={j} className={j < m.impact ? "on" : ""} />
            ));
            return (
              <div
                key={i}
                className={`m-item${done ? " done" : ""}`}
                onClick={() => onMissionClick(i)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onMissionClick(i);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <div
                  className="m-check"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleDone(i);
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={done ? "Mark not done" : "Mark done"}
                >
                  {done ? "✓" : ""}
                </div>
                <div className="m-content">
                  <div className="m-head">
                    <span className="m-title">
                      {m.icon} {m.title}
                    </span>
                    <div className="m-tags">
                      <span className="m-xp">+{m.xp} XP</span>
                      <span className={`m-diff ${m.diff}`}>{DIFF_LABEL[m.diff] ?? m.diff}</span>
                    </div>
                  </div>
                  <div className="m-desc">{m.desc}</div>
                  <div className="m-meta">
                    <span>
                      Impact <span className="m-impact-dots">{dots}</span>
                    </span>
                    <span>{m.metric}</span>
                  </div>
                  {note && (
                    <div className="m-note-preview">
                      💬 "{note.length > 60 ? note.slice(0, 60) + "…" : note}"
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
